import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { BarChart3 } from "lucide-react";
import { uploadWorkbook } from "./eaImportApi";
import EaImportDropzone from "./EaImportDropzone";
import EaImportRow from "./EaImportRow";
import EaSupplyPoints from "./EaSupplyPoints";

const ACTIVE = ["QUEUED", "PROCESSING"];

export const EaImportPanel = ({ company, askConfirm }) => {
  const [jobs, setJobs] = useState([]);
  const [points, setPoints] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    const [j, p] = await Promise.all([api.get("/ea/imports"), api.get("/ea/supply-point-years")]);
    setJobs(j.data);
    setPoints(p.data);
  }, []);

  useEffect(() => {
    load();
    api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  }, [load]);

  const busy = jobs.some((j) => ACTIVE.includes(j.status));
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [busy, load]);

  const onFiles = (files) => {
    if (!company) return toast.error("Seleziona prima il cliente in alto");
    askConfirm(`${files.length} file di controllo fatture`, () => doFiles(files));
  };
  const doFiles = async (files) => {
    for (const f of files) {
      setUploading({ name: f.name, pct: 0 });
      try {
        await uploadWorkbook(f, (pct) => setUploading({ name: f.name, pct }), company.id);
        await load();
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "upload non riuscito"}`);
      }
    }
    setUploading(null);
  };

  return (
    <div className="card-elevated p-5 space-y-4" data-testid="ea-import-panel">
      <div>
        <div className="font-display font-semibold flex items-center gap-2">
          <BarChart3 size={18} className="text-purple-600" /> Importa Controlli Fatture · Energy Analytics
        </div>
        <p className="text-xs text-slate-500 mt-1">
          I file vengono letti uno alla volta e poi eliminati: restano solo i dati estratti (consumi, fatture, curve, PUN, coperture).
        </p>
      </div>
      <EaImportDropzone onFiles={onFiles} disabled={!!uploading || !company} />
      {uploading && (
        <div className="text-xs text-slate-600" data-testid="ea-upload-progress">
          Upload {uploading.name}: {uploading.pct}%
          <div className="h-1.5 bg-slate-100 rounded mt-1"><div className="h-1.5 bg-purple-500 rounded" style={{ width: `${uploading.pct}%` }} /></div>
        </div>
      )}
      <div className="space-y-2" data-testid="ea-import-list">
        {jobs.slice(0, showAll ? 50 : 5).map((j) => <EaImportRow key={j.id} job={j} companies={companies} onChanged={load} />)}
        {jobs.length > 5 && (
          <button onClick={() => setShowAll(!showAll)} className="text-xs font-semibold text-purple-700 hover:underline" data-testid="ea-import-show-all">
            {showAll ? "Mostra solo gli ultimi 5" : `Mostra tutti (${jobs.length})`}
          </button>
        )}
      </div>
      <EaSupplyPoints rows={points} onChanged={load} />
    </div>
  );
};

export default EaImportPanel;
